import { snakeCase } from 'lodash';

import useAxios from '@/use/useAxios';

import store from '@/api/stock';
import material from '@/api/material';
import supplier from '@/api/supplier';
import purchaseRequest from '@/api/purchase-request';
import purchaseOrder from '@/api/purchase-order';
import itemGroup from '@/api/item-group';
import stockLocation from '@/api/stock-location';
import job from '@/api/job';
import stockRequests from '@/api/item-request';
import subJob from '@/api/sub-job';
import expense from '@/api/expense';
import customer from '@/api/customer';
import budget from '@/api/budget';
import user from '@/api/user';
import role from '@/api/role';
import withdraw from '@/api/withdraw';
import dashboard from '@/api/dashboard';
import lot from '@/api/lot';
import notifyGroup from '@/api/notify';
import address from '@/api/address';

export const { axios, setAuthorization, removeAuthorization } = useAxios();

export function sortParamToQuery(sortParams: { field: string; order: string }[]): string {
  let result = '';
  sortParams.forEach((sortParam: { field: string; order: string }) => {
    if (!sortParam || sortParam.field === undefined) result += '';
    result = `${result}&sort=${sortParam.order === 'DESC' ? '-' : ''}${sortParam.field}`;
  });
  return result;
}

export function filterValueToQuery(filter: any) {
  if (!filter) return '';
  let result = '';
  Object.entries(filter).forEach(([key, value]: any[]) => {
    if (key === 'orderBy') result += sortParamToQuery(value);
    else if (Array.isArray(filter[key])) {
      // In case of recent variable is type of Array, then loop through whole array element;
      // eslint-disable-next-line
      filter[key].forEach((elm: any) => {
        // If inside of Array is object, call nested;
        if (typeof elm === 'object' && !(Array.isArray(elm))) result = filterValueToQuery(elm);
        // if not just added;
        result = `${result}&${key}[]=${elm}`;
      });
    } else if (value !== null && value !== '' && value !== undefined) result = `${result}&${key}=${value}`;
  });
  return result;
}

export function camelObjectToSnakeObject(object: any, except: string[] = []) {
  const output = {};
  // eslint-disable-next-line
  Object.keys(object).forEach((key: string) => {
    let newKey = key;
    // Add condition for empty string field and support only remarks, because i'm lazy to fix backend.
    if (key === 'remarks' && (object as any)[key] === '') {
      (output as any)[newKey] = object[key];
      return;
    }
    // eslint-disable-next-line
    if ((!((object as any)[newKey]) || (object as any)[newKey] === '') && ((object as any)[newKey] !== 0 && (object as any)[newKey] !== false)) return;
    if (!(except.indexOf(key) > -1)) newKey = snakeCase(key);

    if (typeof object[key] === 'object' && !Array.isArray(object[key])) {
      // To check is recent variable is type of object and not array
      (output as any)[newKey] = camelObjectToSnakeObject(object[key], except);
      // ---
    } else if (Array.isArray(object[key])) {
      // In case of recent variable is type of Array, then loop through whole array element;
      (output as any)[newKey] = object[key].map((elm: any) => {
        // If inside of Array is object, call nested;
        if (typeof elm === 'object') return camelObjectToSnakeObject(elm, except);
        // if not just return;
        return elm;
      });
    } else {
      (output as any)[newKey] = object[key];
    }
  });
  return output;
}

export const {
  getMaterials,
  getMaterial,
  getMaterialsStats,
  getMaterialPurchaseHistories,
  getMaterialUsageHistories,
  createMaterial,
  updateMaterial,
  deleteMaterial,
} = material(axios);

export const {
  getSuppliers,
  getSupplier,
  createSupplier,
  updateSupplier,
  deleteSupplier,
  checkExistingSupplierCode,
} = supplier(axios);

export const {
  getPurchaseRequests,
  getPurchaseRequest,
  createPurchaseRequest,
  updatePurchaseRequest,
  searchPurchaseRequestItems,
  getPurchaseRequestItems,
  getPurchaseRequestMiscItems,
  approvePurchaseRequest,
  getPurchaseRequestStats,

  receivePurchaseRequest,
} = purchaseRequest(axios);

export const {
  getPurchaseOrders,
  getPurchaseOrder,
  getPurchaseOrderStats,
  createPurchaseOrder,
  updatePurchaseOrder,
  updatePurchaseOrderFill,

  getPurchaseOrderItems,
  getPurchaseOrderMiscItems,
  getPurchaseOrderItemsUnion,
  searchPurchaseOrderItems,
  updatePurchaseOrderItem,
  updatePurchaseOrderMiscItem,

  getPurchaseOrderLogs,

  approvePurchaseOrder,
  revisePurchaseOrder,
  checkExistingPurchaseOrderCode,
} = purchaseOrder(axios);

export const {
  getItemGroups,
  getItemGroup,
  createItemGroup,
  updateItemGroup,
  deleteItemGroup,
} = itemGroup(axios);

export const {
  getStockLocations,
  getStockLocation,
  createStockLocation,
  updateStockLocation,
  deleteStockLocation,
} = stockLocation(axios);

export const {
  getJobs,
  getJob,
  getLatestJob,
  createJob,
  updateJob,
  deleteJob,
  getJobStats,
} = job(axios);

export const {
  getStockRequest,
  cancelStockRequest,
  getStockRequestView,
} = stockRequests(axios);

export const {
  getExpenses,
  getExpenseTypes,
  getExpense,
  createExpense,
  updateExpense,
  deleteExpense,
} = expense(axios);

export const {
  getSubJobs,
  getSubJobsForInternalTab,
  getSubJob,
  searchSubJob,
  createSubJob,
  updateSubJob,
  massUpdateSubJob,
  duplicateSubJob,
  deleteSubJob,
  createSubJobMany,

  updateSubJobAmount,
  storeUpdateSubJobAmount,

  updateSubJobProcessStatus,
  deleteProcessLog,
} = subJob(axios);

export const {
  getCustomers,
  getCustomer,
  getCustomersStats,
  createCustomer,
  updateCustomer,
  deleteCustomer,
  checkExistingCustomerCode,
} = customer(axios);

export const {
  getBudgets,
  getBudget,
  createBudget,
  updateBudget,
  deleteBudget,
} = budget(axios);

export const {
  getMe,
  getUsers,
  getUser,
  createUser,
  updateUser,
  deleteUser,
  deleteSelfSignature,
  deleteSignature,
  checkInformationExists,
} = user(axios);

export const {
  getRoles,
  getRole,
  createRole,
  updateRole,
  deleteRole,
  getPermissions,
} = role(axios);

export const {
  getWithdraws,
  getWithdraw,
  getWithdrawStats,
  getWithdrawItems,
  searchWithdrawItems,
  createWithdraw,
  updateWithdraw,
  updateWithdrawFill,
  deleteWithdraw,

  updateWithdrawItem,
} = withdraw(axios);

export const {
  getDashboard,
} = dashboard(axios);

export const {
  getLots,
  getLot,
  deleteLot,
} = lot(axios);

export const {
  getNotifyGroups,
  getNotifyGroup,
  createNotifyGroup,
  updateNotifyGroup,
  deleteNotifyGroup,
} = notifyGroup(axios);

export const {
  getProvinces,
  getDistricts,
  getSubdistricts,
} = address(axios);

interface LoginPayload {
  username: string;
  password: string;
}

async function refreshToken() {
  console.log('Refresh token was called', (new Date()).toUTCString());
  const response = await axios.post('/api/auth/refresh');
  try {
    if (response.status === 200) {
      setAuthorization(response.data.access_token);
      return true;
    }
  } catch (e) {
    console.error('[api/index] refreshToken Error', e);
  }
  return null;
}

let refreshTokenInterval: any = null;

export const login = async (payload: LoginPayload) => {
  const response = await axios.post('/api/auth/login', payload);
  try {
    if (response.status === 200) {
      setAuthorization(response.data.access_token);
      if (response.data.expires_in > 0) {
        refreshTokenInterval = setInterval(refreshToken, (response.data.expires_in * 100));
      }
      return true;
    }
  } catch (e) {
    console.error('[api/index] login Error', e);
  }
  return null;
};

export async function logout() {
  const response = await axios.post('/api/auth/logout');
  try {
    if (response.status === 200) {
      clearInterval(refreshTokenInterval);
      removeAuthorization();
      return true;
    }
  } catch (e) {
    console.error('[api/index] refreshToken Error', e);
  }
  return null;
}
